<script setup lang="ts">
import {kapitelDateString} from "@/graphql/kapitelTypes"
import {CalendarDate, generateCalendarDate} from "@/helper/calendar/calendarDate"
import {
    addDays,
    endOfMonth,
    endOfWeek,
    isAfter,
    isBefore,
    startOfMonth,
    startOfWeek,
    subDays,
    weekdays
} from "@/helper/datetime/date"
import {computed, PropType} from "vue";

const props = defineProps({
    // any date within the month
    date: {
        type: String as PropType<kapitelDateString>,
        required: true
    },
    showWeeks: {
        type: Boolean,
        required: false,
        default: true
    },
    showWeekdays: {
        type: Boolean,
        required: false,
        default: true
    },
    showContent: {
        type: Boolean,
        required: false,
        default: true
    },
    isContentSameAsDateBefore: {
        type: Function,
        required: false,
        default: (date:CalendarDate, dateBefore:CalendarDate) => false
    },
})

const emit = defineEmits({
    'date-click': (param: {date:kapitelDateString}) => true
})
const onClick = (date: kapitelDateString|undefined) => {
    if(date){
        emit("date-click", {date: date})
    }
}

type CellType = 'date' | 'weekday' | 'week'
type Cell = {
    type: CellType
    date?: CalendarDate
    dateBefore?: CalendarDate,
    sameContentAsDateBefore?: boolean
    label?: string,
    adjacentMonth?: boolean,
    weekend?: boolean,
    today?: boolean
}

// Add weekday row

const cells = computed(() => {
    const cells = [] as Cell[]
    const firstOfMonth = startOfMonth(props.date)
    const lastOfMonth = endOfMonth(props.date)
    const firstDate = startOfWeek(firstOfMonth)
    const lastDate = endOfWeek(lastOfMonth)
    let iterator = firstDate

    if (props.showWeekdays) {
        if (props.showWeeks) {
            // empty weekday cell
            cells.push({
                type: 'weekday',
                label: ''
            })
        }

        weekdays().forEach(weekday => {
            cells.push({
                type: 'weekday',
                label: weekday.labelShort
            })
        })
    }
    
    while (iterator <= lastDate) {
        const calendarDate = generateCalendarDate(iterator)
        const dateBefore = generateCalendarDate(subDays(iterator, 1))

        if (props.showWeeks && calendarDate.isFirstOfWeek) {
            cells.push({
                type: 'week',
                label: calendarDate.week.toString()
            })
        }

        cells.push({
            type: 'date',
            date: calendarDate,
            dateBefore: dateBefore,
            sameContentAsDateBefore: props.isContentSameAsDateBefore(calendarDate, dateBefore) && cells[cells.length - 1].type === 'date',
            label: calendarDate.day.toString(),
            adjacentMonth: isBefore(iterator, firstOfMonth) || isAfter(iterator, lastOfMonth),
            weekend: calendarDate.isWeekend,
            today: calendarDate.isToday
        })

        iterator = addDays(iterator, 1)
    }

    return cells
});
</script>

<template>
<div
    class="month-calendar"
    :class="{'show-weeks': props.showWeeks}"
>
    <div
        class="cell"
        :class="{
            'adjacent-month': cell.adjacentMonth,
            [cell.type]: true,
            'weekend': cell.weekend,
            'today': cell.today,
            'same-as-before': cell.sameContentAsDateBefore,
            'show-content': props.showContent,
        }"
        v-for="cell in cells"
        v-on:click="() => onClick(cell.date?.id)"
    >
        <div class="background" :class="{'same-as-before': cell.sameContentAsDateBefore}">
            <slot
                name="background"
                :date="cell.date"
                :dateBefore="cell.dateBefore"
                :type="cell.type"
                :adjacent="cell.adjacentMonth"
                :sameAsBefore="cell.sameContentAsDateBefore"
            />
        </div>
        <div class="label">
            <slot
                name="label"
                :date="cell.date"
                :dateBefore="cell.dateBefore"
                :type="cell.type"
                :adjacent="cell.adjacentMonth"
                :sameAsBefore="cell.sameContentAsDateBefore"
            >
                {{ cell.label }}
            </slot>
        </div>
        <div v-if="showContent" class="content" :class="{'same-as-before': cell.sameContentAsDateBefore}">
            <slot
                v-if="$slots.date && cell.date"
                name="date"
                :date="cell.date"
                :dateBefore="cell.dateBefore"
                :type="cell.type"
                :adjacent="cell.adjacentMonth"
                :sameAsBefore="cell.sameContentAsDateBefore"
            />
            <slot
                v-else
                name="default"
                :date="cell.date"
                :dateBefore="cell.dateBefore"
                :type="cell.type"
                :adjacent="cell.adjacentMonth"
                :sameAsBefore="cell.sameContentAsDateBefore"
            />
        </div>
    </div>
</div>
</template>

<style lang="scss" scoped>
.month-calendar {
    display: flex;
    flex-wrap: wrap;

    > .cell {
        min-height: 3em; // Twinki said so
    }

    > .cell {
        flex: 0 1 calc(100% / 7);
    }

    &.show-weeks {
        > .cell {
            flex: 0 1 calc(100% / 8);
        }
    }

    > .cell {
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;

        &:before{
            content: '';
            display: block;
            padding-top: 100%;
        }

        > * {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            color: inherit;
        }

        &.show-content:not(.weekday) {
            > .label,
            > .content {
                bottom: 1em;
            }
        }

        &.weekday,
        &.week {
            color: var(--ion-color-grey);
        }

        &.weekend {
            color: var(--ion-color-grey-tint);
            font-weight: var(--custom-font-weight-medium);
        }

        &.today {
            color: var(--ion-color-red);
            font-weight: var(--custom-font-weight-medium);
        }

        &.adjacent-month {
            color: var(--ion-color-grey-tint-1);
        }
    }
}
</style>
